import React, { useState } from "react";
import { SearchIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FaUserPlus, FaUsers } from "react-icons/fa";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { getPatientsApi } from "../../api/patients";
import LayoutView from "../../components/layout";
import Auth from "../../hoc/Auth";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { calculateAge } from "../../utils/dob";

const Patients = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebouncedValue(searchText);
  const patientsQuery = useQuery(
    ["getPatientsApi", debouncedSearch],
    () => getPatientsApi(debouncedSearch),
    { keepPreviousData: true },
  );
  const patients = patientsQuery.data?.data || [];

  return (
    <LayoutView activePage="Patients">
      <Box maxW="1200px" mx="auto">
        <Flex
          justify="space-between"
          align={{ base: "stretch", md: "flex-end" }}
          direction={{ base: "column", md: "row" }}
          gap="4"
        >
          <Box>
            <Heading size="md" color="gray.800">
              Patient records
            </Heading>
            <Text mt="1" color="gray.500">
              Search, review and manage registered patients.
            </Text>
          </Box>
          <Button
            leftIcon={<FaUserPlus />}
            onClick={() => navigate("/patient/register-patient")}
          >
            Register patient
          </Button>
        </Flex>

        <Box
          mt="6"
          bg="white"
          borderRadius="2xl"
          boxShadow="card"
          overflow="hidden"
        >
          <Flex
            p={{ base: "4", md: "6" }}
            borderBottomWidth="1px"
            borderColor="gray.100"
          >
            <InputGroup maxW="480px" size="lg">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search name, OAUH ID, unit, matric or NHIS no."
                aria-label="Search patients"
              />
            </InputGroup>
          </Flex>

          {patientsQuery.isLoading ? (
            <Center minH="320px">
              <Spinner color="brand.500" size="lg" />
            </Center>
          ) : patientsQuery.isError ? (
            <Center
              minH="320px"
              flexDirection="column"
              px="6"
              textAlign="center"
            >
              <Text fontWeight="700">Unable to load patients</Text>
              <Text mt="2" color="gray.500">
                Check your connection and try again.
              </Text>
              <Button
                mt="4"
                variant="outline"
                onClick={() => patientsQuery.refetch()}
              >
                Try again
              </Button>
            </Center>
          ) : !patients.length ? (
            <Center
              minH="320px"
              flexDirection="column"
              px="6"
              textAlign="center"
            >
              <Center
                w="56px"
                h="56px"
                borderRadius="full"
                bg="brand.50"
                color="brand.600"
              >
                <Icon as={FaUsers} boxSize="6" />
              </Center>
              <Text mt="4" fontWeight="700">
                No patients found
              </Text>
              <Text mt="1" color="gray.500">
                {searchText
                  ? "Try a different search term."
                  : "Add the first patient to get started."}
              </Text>
            </Center>
          ) : (
            <TableContainer>
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Patient ID</Th>
                    <Th>Name</Th>
                    <Th>Gender</Th>
                    <Th>Age</Th>
                    <Th>Phone</Th>
                    <Th>State</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {patients.map((patient) => (
                    <Tr key={patient._id} _hover={{ bg: "brand.50" }}>
                      <Td>
                        <Badge
                          colorScheme="blue"
                          px="2"
                          py="1"
                          borderRadius="md"
                        >
                          {patient.patientNumber || patient._id?.slice(-6)}
                        </Badge>
                      </Td>
                      <Td fontWeight="700" textTransform="capitalize">
                        {patient.bioData?.surname} {patient.bioData?.otherNames}
                      </Td>
                      <Td textTransform="capitalize">
                        {patient.bioData?.sex || "—"}
                      </Td>
                      <Td>
                        {calculateAge(patient.bioData?.dateOfBirth) || "—"}
                      </Td>
                      <Td>{patient.bioData?.telephone || "—"}</Td>
                      <Td>{patient.bioData?.state || "—"}</Td>
                      <Td textAlign="right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/patient/${patient._id}`)}
                        >
                          View record
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </LayoutView>
  );
};

export default Auth(Patients);
